/**
 * If a renderer crash restored an earlier history entry, send the user back
 * to the armed `/chats/:token` without using `history.back()`.
 *
 * Recovery itself runs once per document load (and on bfcache restore). It
 * does not run on later client-side navigations, so an intentional visit is
 * left alone. The location watcher skips only the recovery hop. Any later
 * navigation, including a return to the path the document loaded on, disarms
 * a record the error boundary kept armed. A full-document departure disarms
 * via `pagehide`; a renderer kill does not fire that event.
 */

import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  consumeChatCrashRecovery,
  disarmChatCrashRestoreIfLeft,
  noteChatDocumentHidden,
  noteChatDocumentShown,
  pathnameOf,
  reportChatPageCrash,
  type ChatCrashRecovery,
} from './chatCrashRestore'

function applyRecovery(
  recovery: ChatCrashRecovery,
  navigate: (href: string, opts: { replace: true }) => void,
): void {
  reportChatPageCrash({
    source: 'history_restore',
    roomToken: recovery.roomToken,
    studyId: recovery.studyId,
    sendStarted: recovery.sendStarted,
  })
  if (recovery.action === 'restore') {
    navigate(recovery.href, { replace: true })
  }
}

export function ChatCrashRestore() {
  const location = useLocation()
  const navigate = useNavigate()
  const didCheckOnLoad = useRef(false)
  const loadPath = useRef<string | null>(null)
  const pendingRecoveryPath = useRef<string | null>(null)

  useEffect(() => {
    if (didCheckOnLoad.current) return
    didCheckOnLoad.current = true
    const recovery = consumeChatCrashRecovery(location.pathname)
    if (!recovery) return
    if (recovery.action === 'restore') {
      pendingRecoveryPath.current = pathnameOf(recovery.href)
    }
    applyRecovery(recovery, navigate)
  }, [location.pathname, navigate])

  // Independent of `navigate`. Skip the load observation and the single
  // recovery hop that follows it. A later visit to that same load path
  // (Results → recovered chat → back to Results) disarms.
  useEffect(() => {
    if (loadPath.current === null) {
      loadPath.current = location.pathname
      return
    }
    if (pendingRecoveryPath.current && location.pathname === loadPath.current) {
      return
    }
    if (pendingRecoveryPath.current && location.pathname === pendingRecoveryPath.current) {
      pendingRecoveryPath.current = null
      return
    }
    pendingRecoveryPath.current = null
    disarmChatCrashRestoreIfLeft(location.pathname)
  }, [location.pathname])

  useEffect(() => {
    function onPageHide(event: PageTransitionEvent) {
      noteChatDocumentHidden(event.persisted === true)
    }
    function onPageShow(event: PageTransitionEvent) {
      if (!event.persisted) return
      noteChatDocumentShown(true, window.location.pathname)
      const recovery = consumeChatCrashRecovery(window.location.pathname)
      if (!recovery) return
      applyRecovery(recovery, navigate)
    }
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('pageshow', onPageShow)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [navigate])

  return null
}
